import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Plus, Move, Trash2 } from 'lucide-react';
import { classNames } from '../../lib/utils';
import { calculateIkrProgress } from '../../lib/utils';
import ProgressDonut from '../../components/ui/ProgressDonut';
import KeyResultRow from './KeyResultRow';

export default function ObjectiveCard({ objective, onUpdate, onDelete, users, teams, viewer, selectedTeamId }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(objective.objective === 'New Objective');
  const [objectiveText, setObjectiveText] = useState(objective.objective);
  const isLocked = viewer.role !== 'Admin';

  const handleUpdateObjectiveField = async (field, value) => {
    await onUpdate(objective.id, { [field]: value });
  };

  const handleUpdateKr = async (updatedKr) => {
    const updatedKeyResults = objective.keyResults.map(kr => kr.id === updatedKr.id ? updatedKr : kr);
    await handleUpdateObjectiveField('keyResults', updatedKeyResults);
  };

  const handleDeleteKr = async (krId) => {
    const updatedKeyResults = objective.keyResults.filter(kr => kr.id !== krId);
    await handleUpdateObjectiveField('keyResults', updatedKeyResults);
  };

  const addKeyResult = async () => {
    const newKr = { id: `kr_${Date.now()}`, text: 'New Team Key Result', teamId: '', kpiTarget: 0, kpiUnit: '', individualKRs: [] };
    const updatedKeyResults = [...(objective.keyResults || []), newKr];
    await handleUpdateObjectiveField('keyResults', updatedKeyResults);
  };

  const team = objective.primaryTeamId ? teams[objective.primaryTeamId] : null;

  const overallProgress = useMemo(() => {
    if (!objective.keyResults || objective.keyResults.length === 0) return 0;
    const krProgresses = objective.keyResults.map(kr => {
      const individualKRs = kr.individualKRs || [];
      if (individualKRs.length === 0) return 0;
      return individualKRs.reduce((sum, ikr) => sum + calculateIkrProgress(ikr), 0) / individualKRs.length;
    });
    return krProgresses.reduce((sum, p) => sum + p, 0) / krProgresses.length;
  }, [objective.keyResults]);

  const handleMoveObjective = (e) => {
    const newPrimaryTeamId = e.target.value === 'null' ? null : e.target.value;
    const newAssociatedTeamIds = newPrimaryTeamId ? [newPrimaryTeamId] : [];
    onUpdate(objective.id, { primaryTeamId: newPrimaryTeamId, associatedTeamIds: newAssociatedTeamIds });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg mb-6 border border-gray-200/80 transition-shadow hover:shadow-xl">
      <div className="p-4 flex justify-between items-start">
        <div className="flex-grow flex items-start">
          <button onClick={() => setIsExpanded(!isExpanded)} className="p-2 mr-2 rounded-full hover:bg-gray-100 text-gray-500">
            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>
          <div className="flex-grow">
            {isEditing ? (
              <input type="text" value={objectiveText} onChange={(e) => setObjectiveText(e.target.value)} onBlur={() => { setIsEditing(false); handleUpdateObjectiveField('objective', objectiveText); }} onKeyPress={(e) => { if (e.key === 'Enter') { setIsEditing(false); handleUpdateObjectiveField('objective', objectiveText); } }} className="w-full p-1 border rounded text-lg font-semibold" autoFocus />
            ) : (
              <h3 className={classNames('text-lg font-semibold text-gray-800', !isLocked && 'hover:text-blue-600 cursor-pointer')} onClick={() => !isLocked && setIsEditing(true)}>{objective.objective}</h3>
            )}
            {team ? <span className="text-sm font-medium bg-indigo-100 text-indigo-700 rounded-full px-3 py-1 mt-2 inline-block">{team.name}</span> : <span className="text-sm font-medium bg-purple-100 text-purple-700 rounded-full px-3 py-1 mt-2 inline-block">Company Wide</span>}
          </div>
        </div>
        <div className="flex items-center space-x-3 flex-shrink-0 ml-4">
          <ProgressDonut progress={overallProgress} />
          {viewer.role === 'Admin' && (
            <>
              <div className="relative">
                <Move size={18} className="text-gray-500 cursor-pointer" />
                <select onChange={handleMoveObjective} value={objective.primaryTeamId || 'null'} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer">
                  <option value="null">Company Wide</option>
                  {Object.entries(teams).map(([id, team]) => <option key={id} value={id}>{team.name}</option>)}
                </select>
              </div>
              <button onClick={() => onDelete(objective.id)} className="text-gray-400 hover:text-red-600 p-2 rounded-full hover:bg-red-100 transition-colors">
                <Trash2 size={18} />
              </button>
            </>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="p-4 border-t border-gray-200">
          <div className="grid grid-cols-12 gap-x-4 items-center mb-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
            <div className="col-span-1" />
            <div className="col-span-4">Team Key Result</div>
            <div className="col-span-2">Lead Team</div>
            <div className="col-span-2">Target KPI</div>
            <div className="col-span-3 text-right">Progress</div>
          </div>
          {(objective.keyResults || []).map(kr => (
            <KeyResultRow key={kr.id} kr={kr} onUpdate={handleUpdateKr} onDeleteKr={handleDeleteKr} users={users} teams={teams} isLocked={isLocked} viewer={viewer} selectedTeamId={selectedTeamId} />
          ))}
          {viewer.role === 'Admin' && (
            <div className="mt-4 pl-8">
              <button onClick={addKeyResult} className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-semibold">
                <Plus size={16} className="mr-1" /> Add Team KR
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
